import search from "./search";

export function filterCatalog(term, catalogById) {
  const catalog = Object.keys(catalogById).map(id => catalogById[id]);
  if (!term) {
    return catalog;
  }
  const results = new Set();
  const matching = catalog.filter(item => search(item.name, term));

  if (matching.length === 0) {
    return [];
  }

  const childrenByParentId = catalog.reduce((map, item) => {
    if (item.parentId == null) {
      return map;
    }

    map[item.parentId] = map[item.parentId] || [];
    map[item.parentId].push(item);
    return map;
  }, {});

  matching.forEach(item => results.add(item.id));
  matching.filter(item => item.parentId != null)
    .flatMap(item => getParents(item, catalogById))
    .filter(item => !!item)
    .forEach(item => results.add(item.id));
  matching.flatMap(item => getDescendants(item, childrenByParentId))
    .forEach(item => results.add(item.id));

  return Array.from(results).map(id => catalogById[id]);
}

function getDescendants(item, childrenByParentId, seen = new Set()) {
  const children = childrenByParentId[item.id] || [];

  return children.flatMap(child => {
    if (seen.has(child.id)) {
      return [];
    }

    seen.add(child.id);
    return [child, ...getDescendants(child, childrenByParentId, seen)];
  });
}

export function buildCatalogTree(catalog) {
  if (!catalog) return [];
  // Sorted as a copy: the catalog passed in is often the one in the redux store. Children are found in the copy too,
  // so they come out in name order.
  const sorted = [...catalog].sort((a, b) => a.name.localeCompare(b.name));
  const roots = [];
  const childrenByParentId = new Map();

  // One pass over the sorted copy: a node's children are appended in name order, so no re-sorting is needed later.
  for (const item of sorted) {
    if (!item.parentId) {
      roots.push(item);
      continue;
    }

    const siblings = childrenByParentId.get(item.parentId);
    if (siblings) {
      siblings.push(item);
    }
    else {
      childrenByParentId.set(item.parentId, [item]);
    }
  }

  return roots.map(item => buildTreeFromNode(item, childrenByParentId));
}

export function buildCatalogMap(catalog) {
  if (!catalog) return {};
  return catalog.reduce((map, item) => {
    map[item.id] = item;
    return map;
  }, {});
}

function buildTreeFromNode(item, childrenByParentId) {
  return {
    ...item,
    title: item.name,
    subtitle: item.type.name,
    expanded: true,
    children: (childrenByParentId.get(item.id) || []).map(child => buildTreeFromNode(child, childrenByParentId))
  };
}

export function getParents(item, catalogById) {
  const path = [];

  while (item && item.parentId) {
    item = catalogById[item.parentId];
    if (!item) break;
    path.push(item);
  }

  return path;
}

export function normalizeTree(tree, parentId = null) {
  return {
    ...tree,
    parentId,
    children: tree.children.map(child => normalizeTree(child, tree.id))
  };
}

export function flattenTree(tree) {
  return tree
    .map(({children, ...item}) => {
      if(children.length < 1) {
        return [item];
      }

      return [item, ...flattenTree(children)];
    })
    .reduce((flatMap, next) => flatMap.concat(next), []);
}

export function buildNameListFromTree(baseName, parentId, tree) {
  return tree.reduce((list, item) => {
    const rootName = baseName ? baseName + " → " : "";
    const id = parentId ? parentId + ", " : '';
    list.push({name: rootName + item.name, id: id + item.id});
    return list.concat(buildNameListFromTree(rootName + item.name, id + item.id, item.children));
  }, []);
}

export function buildNameListFromTreeForAnswers(baseName, parentId, tree) {
  return tree.reduce((list, item) => {
    const rootName = baseName ? baseName + " → " : "";
    const id = parentId ? parentId + "," : '';
    list.push({name: rootName + item.name, id: id + item.id});
    return list.concat(buildNameListFromTreeForAnswers(rootName + item.name, id + item.id, item.children));
  }, []);
}

export function getFriendlyCatalogPathName(item, catalogById) {
  if (!item) return '';
  const path = [item];

  while (item.parentId) {
    if (!catalogById[item.parentId]) {
      break;
    }

    item = catalogById[item.parentId];
    path.push(item);
  }

  return path.reverse().map(item => item.name).join(' → ');
}